/**
 * Site-wide configuration and brand constants.
 * Pulls from env where available so the same build works across environments.
 */
import type { NavLink } from '@/types/content';

const env = import.meta.env;

export const site = {
  name: 'FIT D',
  fullName: 'FIT D Fashion — Rehab • Rebuild • Perform',
  tagline: 'Rehab • Rebuild • Perform',
  motto: 'From pain → strength → skill',
  founder: 'Deepak Chaurasiya',
  role: 'Healthcare Fitness & Rehabilitation Specialist',
  description:
    'R2P is a coaching system that takes you from injury and pain to lasting strength and real-world skill — built on the R2P Method by Deepak Chaurasiya.',
  url: env.VITE_SITE_URL ?? 'https://r2pbyfitdfashion.com',
  email: env.VITE_CONTACT_EMAIL ?? 'hello@r2pbyfitdfashion.com',
  // Secondary inbox that also receives enquiries. When set, `mailtoHref()`
  // below adds it as a `cc=` so users who bypass the form (and just click
  // the email address) still reach both inboxes — matching how the server
  // fans CONTACT_TO out to multiple recipients.
  emailCc: env.VITE_CONTACT_EMAIL_CC ?? 'fitdfashion2030@gmail.com',
  contactFormEndpoint: env.VITE_CONTACT_FORM_ENDPOINT ?? '/api/contact',
  // MSG91 OTP widget — public values, embedded client-side.
  msg91: {
    widgetId: env.VITE_MSG91_WIDGET_ID ?? '',
    tokenAuth: env.VITE_MSG91_TOKEN_AUTH ?? '',
  },
  social: {
    instagram: env.VITE_INSTAGRAM_URL ?? 'https://www.instagram.com/r2p.rehab',
    instagramHandle: env.VITE_INSTAGRAM_HANDLE ?? 'r2p.rehab',
    instagramCoach: 'https://www.instagram.com/deepak.r2p',
  },
} as const;

/**
 * Build a `mailto:` URL that CCs the secondary inbox when one is configured.
 * Use everywhere a raw `mailto:${site.email}` would otherwise appear so both
 * inboxes stay in sync with the server-side CONTACT_TO fan-out.
 */
export function mailtoHref(subject?: string): string {
  const params = new URLSearchParams();
  if (site.emailCc) params.set('cc', site.emailCc);
  if (subject) params.set('subject', subject);
  const query = params.toString();
  return `mailto:${site.email}${query ? `?${query}` : ''}`;
}

export const primaryNav: NavLink[] = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'The Method', to: '/method' },
  { label: 'Services', to: '/services' },
  { label: 'Programs', to: '/programs' },
  { label: 'Gallery', to: '/gallery' },
  { label: 'Contact', to: '/contact' },
];
